import type {
	EventBulkDownload,
	EventInstrumentCommentaryUpdate,
	EventInstrumentPropagationUpdate,
	EventInvestmentDraftUpdate,
	EventInvestmentType,
	EventInvestmentUpdate,
	EventLevel,
	EventMarketUpdate,
	EventReportUpdate,
	EventSharedEntity,
	EventTopic,
	EventUserUpdate,
	InvestmentStatus,
	SmartXBlockedPosition,
	SmartXSyncRecordDtoOutcomeEnum,
	SmartXSyncRecordDtoStepEnum,
	SmartXSyncRecordDtoTriggerEnum,
} from "@eu.mdotm.api/sphere";
import type { LabComputeEvent } from "@eu.mdotm.api/portfolio-lab";
import { AbortError } from "@mdotm/utils/promise";
import EventEmitter from "eventemitter3";
import { useEffect, useRef, useState } from "react";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import type { TypedEventEmitter } from "./helper-types";
import { useDebounced } from "@mdotm/shadcn/legacy/react-extensions";

export type StepToUpdate =
	| "MAIN_INFO"
	| "ALLOCATION_CONSTRAINTS"
	| "RISK_CONSTRAINTS"
	| "INVESTABLE_UNIVERSE"
	| "STRATEGY_CONSTRAINTS"
	| "MARKET_VIEW";

/**
 * Payload of the non-portfolio `*-import-update` topics (reference, universe, market view,
 * instruments). As with the investment variant, the backend publishes no generated type for these,
 * so the shape below is the one the event log shows — every field optional, since the only one we
 * ever read is `uuid` (to match the entity a screen is showing) and `status` does not share the
 * `InvestmentStatus` vocabulary across these families.
 */
export type EntityImportUpdate = { uuid?: string; name?: string; status?: string };

/**
 * How a portfolio's sync to its SmartX model ended: sent after every link, and after an accepted
 * proposal only when the sync was blocked or failed. The backend publishes no generated type for it;
 * the enums are the sync record's.
 */
export type EventSmartXSync = {
	level?: EventLevel;
	uuid?: string;
	name?: string;
	trigger?: SmartXSyncRecordDtoTriggerEnum;
	reportedModelId?: string;
	outcome?: SmartXSyncRecordDtoOutcomeEnum;
	/** Where an unsuccessful sync stopped; `null` when it succeeded or an unexpected error stopped it. */
	step?: SmartXSyncRecordDtoStepEnum | null;
	reason?: string | null;
	positionsSent?: number;
	blockedPositions?: SmartXBlockedPosition[];
};

export type ServerEventPayloadMap = {
	[EventTopic.InvestmentUpdate]: EventInvestmentUpdate;
	[EventTopic.MarketUpdate]: EventMarketUpdate;
	[EventTopic.InvestmentDraftConfigurationUpdate]: EventInvestmentDraftUpdate;
	[EventTopic.BenchmarkUpdate]: {
		// TODO: migrate to EventUpdateBenchmark once the definition matches its usage
		level?: EventLevel;
		type?: EventInvestmentType;
		uuid?: string;
		name?: string;
		status?: string;
	};
	[EventTopic.UserUpdate]: EventUserUpdate;
	[EventTopic.BulkReportDownload]: EventBulkDownload;
	[EventTopic.InvestmentBulkEnhanceUpdate]: EventInvestmentDraftUpdate;
	[EventTopic.SharedEntity]: EventSharedEntity;
	[EventTopic.ModifiedEntity]: EventSharedEntity;
	[EventTopic.RemovedEntity]: EventSharedEntity;
	[EventTopic.DeletedEntity]: EventSharedEntity;
	[EventTopic.CommentaryUpdate]: Omit<EventInvestmentUpdate, "hasBreachedAlerts">;
	[EventTopic.InvestmentReportUpdate]: EventReportUpdate;
	[EventTopic.InstrumentCommentaryUpdate]: EventInstrumentCommentaryUpdate;
	/**
	 * Instrument-customization propagation (`MDOT-24644`). All three are customer-scoped and carry
	 * the *full* propagation state, so a listener never has to derive the fields an event does not
	 * mention — apply the payload as-is, guarded by its revision.
	 */
	[EventTopic.InstrumentPropagationScheduleChanged]: EventInstrumentPropagationUpdate;
	[EventTopic.InstrumentPropagationStarted]: EventInstrumentPropagationUpdate;
	[EventTopic.InstrumentPropagationDone]: EventInstrumentPropagationUpdate;
	[EventTopic.InvestmentImportUpdate]: { uuid: string; name: string; status: InvestmentStatus }; // based on event log BE should provide his own type
	// The other four families an import can land in. `import-apply` covers portfolios AND
	// universes/references/market views/instruments, so without these an import the bot (or the
	// UI) starts is reported for portfolios only and finishes silently everywhere else.
	[EventTopic.ReferenceImportUpdate]: EntityImportUpdate;
	[EventTopic.UniverseImportUpdate]: EntityImportUpdate;
	[EventTopic.MarketViewImportUpdate]: EntityImportUpdate;
	[EventTopic.InstrumensImportUpdate]: EntityImportUpdate;
	[EventTopic.ProposalExecutionBreachedConstraint]: EventInvestmentUpdate;
	"system-update": unknown;
	[EventTopic.CashConversionToMoneyMarket]: string;
	[EventTopic.SmartxSync]: EventSmartXSync;
	[EventTopic.LabCompute]: LabComputeEvent;
	[EventTopic.SharedEntities]: {
		investments?: Array<{
			grantorName: string;
			permissionLevel: string; // null
			sharedEntityName: string;
			sharedEntityType: string;
			sharedEntityUuid: string;
		}>;
	};
};

/**
 * This type defines global app events which can be emitted on the
 * event bus.
 *
 * Each key corresponds to an event name, while each corresponding type
 * corresponds to the payload for that event.
 *
 * Example:
 *
 * ```ts
 * export type EventPayloadMap = { myEvent: { myPayloadContent: string } };
 * eventBus.on("myEvent", ({ myPayloadContent }) => console.log(myPayloadContent));
 * eventBus.emit("myEvent", { myPayloadContent: "example" });
 * ```
 */
export type EventPayloadMap = {
	/**
	 * The session could not be recovered: a request was rejected with a 401 and either there was
	 * no session to renew or the renew is not worth attempting again (see `RenewOutcome`). This is
	 * **not** raised for every 401 — the axios interceptor in `auth-interceptors` renews and
	 * replays first, and only reports here once that has failed — so a listener may act on it
	 * directly.
	 */
	unauthorized: void;
	printNextComponent: { name: string; height: number };
	/**
	 * The server-event stream (re)opened. The stream is torn down and rebuilt routinely — on every
	 * access-token renewal and after an error retry — and the restart discards the browser's own
	 * `Last-Event-ID` replay, so listeners that cannot afford to miss a transition use this to
	 * reconcile their state with a fresh read.
	 */
	"server-events-stream-open": void;
	/**
	 * The bot finished writing and the app's caches have been refreshed (MDOT-24442). Emitted by
	 * `botWriteInvalidation`, AFTER the invalidation's refetches have settled, so a listener reading
	 * the cache at this point sees the post-write state.
	 *
	 * Exists for screens that CANNOT be refreshed by an invalidation alone because they copy server
	 * state into client state — the workspace seeds its atoms once per mount, so it needs an explicit
	 * nudge to re-read. Screens that render straight from their query need nothing from this event.
	 *
	 * `uuids` collects every uuid-shaped value found anywhere in the settled tool calls' inputs, so a
	 * listener can tell whether the entity it is showing was among the bot's targets. It is a
	 * best-effort hint, not a guarantee: a tool that names its target by something other than a uuid
	 * (a folder path, a name) contributes nothing, and a background task contributes nothing either
	 * (its input isn't carried through the deferred-delivery path). Listeners should therefore treat
	 * a miss as "can't tell", not as "not affected" — see `toolNames` for the fallback.
	 */
	"bot-write-settled": { toolNames: string[]; uuids: string[] };
	/**
	 * A Portfolio Lab write the bot made in this tab has returned its result, so the server holds it.
	 * Emitted by sphere-ai's `useBotLabWriteResults` once per tool call, for live results only.
	 *
	 * Unlike `bot-write-settled`, nothing has been refreshed yet: the listener reads the server itself.
	 */
	"bot-lab-write-result": { toolName: string };
	/**
	 * SphereAI called `portfolio_lab-recalculate` in this tab's live stream (MDOT-25847): the Lab marks
	 * the columns it names as calculating. `null` columns is every column; `null` scenario is Current
	 * Market. Emitted by sphere-ai's `useBotLabRecalculate` once per call.
	 */
	"bot-lab-recalculate-start": { toolCallId: string; columnUuids: string[] | null; scenarioId: string | null };
	/**
	 * That call's task has settled: each column's outcome as the task reports it, or `null` when the task
	 * failed as a whole. Nothing has been refreshed yet: the listener reads the server itself.
	 */
	"bot-lab-recalculate-settled": {
		toolCallId: string;
		scenarioId: string | null;
		columns: BotLabComputedColumn[] | null;
	};
} & ServerEventPayloadMap;

/** One column's line in a settled `portfolio_lab-recalculate`, a shape kept in step with sphere-mcp by hand. */
export type BotLabComputedColumn = { columnUuid: string; status: LabComputeEvent["status"] };

/**
 * App event bus, used to listen and dispatch events that are not specific to a specific context, e.g.
 * expired authentication/authorization token.
 */
export const eventBus = new EventEmitter<EventPayloadMap>() as unknown as TypedEventEmitter<EventPayloadMap>;

export type EventBusListener<K extends keyof EventPayloadMap> = (payload: EventPayloadMap[K]) => void;

/**
 * Attach to an event on the event bus.
 *
 * @param eventName name of the event.
 * @param listener a callback that will be called with the event payload.
 * @param opt optional configuration.
 */
export function useEventBus<K extends keyof EventPayloadMap>(
	eventName: K,
	listener: EventBusListener<K>,
	opt?: { disabled?: boolean },
): void;

/**
 * Attach to an event on the event bus.
 *
 * @param eventName name of the event.
 * @param config an object containing the callback and a filter function.
 * @param opt optional configuration.
 */
export function useEventBus<K extends keyof EventPayloadMap>(
	eventName: K,
	config: { filter?(payload: EventPayloadMap[K]): boolean; listener: EventBusListener<K> },
	opt?: { disabled?: boolean },
): void;

/**
 * Attach to an event on the event bus.
 *
 * @param eventName name of the event.
 * @param listenerOrConfig a callback that will be called with the event payload or an object containing the callback and a filter function.
 * @param opt optional configuration.
 */
export function useEventBus<K extends keyof EventPayloadMap>(
	eventName: K,
	listenerOrConfig:
		| ((payload: EventPayloadMap[K]) => void)
		| { filter?(payload: EventPayloadMap[K]): boolean; listener: EventBusListener<K> },
	opt?: { disabled?: boolean },
): void;

export function useEventBus<K extends keyof EventPayloadMap>(
	eventName: K,
	listenerOrConfig:
		| EventBusListener<K>
		| { filter?(payload: EventPayloadMap[K]): boolean; listener: EventBusListener<K> },
	opt?: { disabled?: boolean },
): void {
	const listenerOrConfigRef = useUnsafeUpdatedRef(listenerOrConfig);

	useEffect(() => {
		if (opt?.disabled) return;

		function wrappedListener(payload: EventPayloadMap[K]) {
			if (typeof listenerOrConfigRef.current === "function") {
				listenerOrConfigRef.current(payload);
			} else if (!listenerOrConfigRef.current.filter || listenerOrConfigRef.current.filter(payload)) {
				listenerOrConfigRef.current.listener(payload);
			}
		}

		eventBus.on(eventName, wrappedListener);

		return () => {
			eventBus.off(eventName, wrappedListener);
		};
	}, [eventName, listenerOrConfigRef, opt?.disabled]);
}

/**
 *
 * @param eventName name of the event
 * @param listener listener callback with the event payload
 * @param opts set timeout to queue the list
 */
export function useGroupEventBus<K extends keyof EventPayloadMap>(
	eventName: K,
	listener: (payload: Array<EventPayloadMap[K]>) => unknown,
	opts?: {
		/** @default 5000 */
		timeout?: number;
		filter?(payload: EventPayloadMap[K]): boolean;
	},
): void {
	const { timeout = 5000 } = opts ?? {};
	const eventPayloadListRef = useRef<Array<EventPayloadMap[K]>>([]);

	const { invoke } = useDebounced(
		() => {
			listener(eventPayloadListRef.current);
			eventPayloadListRef.current = [];
		},
		{
			debounceInterval: timeout,
			unmountBehavior: "flush",
		},
	);

	const [dispatch] = useState(() => (payload: EventPayloadMap[K]) => {
		eventPayloadListRef.current = [...eventPayloadListRef.current, payload];
		invoke();
	});

	useEventBus(eventName, {
		listener: dispatch,
		filter: opts?.filter,
	});
}

/**
 *
 * @param eventName name of the event
 * @param opts.filter filter callback with the event payload
 * @param opts.signal recive a {@link AbortSignal} to resolve the promise
 * @returns Promise
 */
export function waitForEvent<K extends keyof EventPayloadMap>(
	eventName: K,
	opts?: {
		filter?: (payload: EventPayloadMap[K]) => boolean;
		signal?: AbortSignal;
	},
): Promise<EventPayloadMap[K]> {
	return new Promise<EventPayloadMap[K]>((resolve, reject) => {
		if (opts?.signal?.aborted) {
			reject(opts.signal.reason ?? new AbortError());
			return;
		}
		const cleanup = () => {
			opts?.signal?.removeEventListener("abort", abortHandler);
			eventBus.off(eventName, resolveHandler);
		};
		const abortHandler = () => {
			cleanup();
			reject(opts?.signal?.reason ?? new AbortError());
		};

		const resolveHandler = (payload: EventPayloadMap[K]) => {
			if (!opts?.filter || opts.filter(payload)) {
				cleanup();
				resolve(payload);
			}
		};
		opts?.signal?.addEventListener("abort", abortHandler);
		eventBus.on(eventName, resolveHandler);
	});
}
